import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  usePushProjectHistory,
  useRetrieveProjectHistory,
  useSearch,
} from "@planzone/view-models";
import { Identifiable, Project, SearchQuery, searchQuery } from "@planzone/dto";
import { VisibilityWatcher } from "@common/visibilityWatcher";

import { Props } from "./types";
import { ToggleArrow } from "./toggleArrow";
import { Search } from "./search";
import { SearchResults } from "./searchResults";
import { ProjectIcon } from "./projectIcon";
import { History } from "./history";
import styles from "./styles.scss";
import { SelectList } from "@common/selectList";

function ProjectHistory({ selectProject }: Props) {
  const query: SearchQuery = { ...searchQuery, limit: 10, types: ["project"] };
  const { t } = useTranslation();
  const push = useNavigate();
  const [name, setName] = React.useState("");
  const {
    history,
    retrieveProjectHistory,
    isRequestPending: isRetrieveRequestPending,
    isRequestSuccess: isRetrieveRequestSuccess,
  } = useRetrieveProjectHistory();
  const {
    isRequestPending: isPushRequestPending,
    isRequestSuccess: isPushRetrieveRequestSuccess,
  } = usePushProjectHistory();
  const {
    isRequestPending: isSearchRequestPending,
    searchResults,
    index,
    canSearch,
    search,
  } = useSearch({ ...query, name });

  React.useEffect(() => {
    !isRetrieveRequestPending &&
      !isRetrieveRequestSuccess &&
      retrieveProjectHistory();
  }, []);

  React.useEffect(() => {
    !isPushRequestPending &&
      isPushRetrieveRequestSuccess &&
      retrieveProjectHistory();
  }, [isPushRequestPending, isPushRetrieveRequestSuccess]);

  return (
    <SelectList listClassName={styles.listRoot}>
      {({ isOpen, show, hide }) => ({
        input: (
          <div
            className={classNames(styles.projectHistory, {
              [styles.open]: isOpen,
            })}
          >
            <div className={styles.input} onClick={isOpen ? hide : show}>
              <ProjectIcon className={styles.projectIcon} />
              <p>{t("search.title")}</p>
              <ToggleArrow className={styles.toggleIcon} />
            </div>
          </div>
        ),
        list: (
          <div className={styles.main}>
            <div className={styles.search}>
              <Search
                name={name}
                edit={setName}
                submit={(name) => {
                  push(`/search?query=${name}`);
                  setName("");
                  hide();
                }}
                search={(name) => search({ ...query, name, limit: 10 })}
              />
            </div>
            <div className={styles.list}>
              {name.length > 0 ? (
                <SearchResults
                  query={name}
                  projects={
                    searchResults.map(
                      ({ args }) => args,
                    ) as Identifiable<Project>[]
                  }
                  canLoadMore={canSearch}
                  select={(project) => {
                    selectProject(project);
                    hide();
                  }}
                  loadMore={() =>
                    canSearch &&
                    search({
                      ...query,
                      name,
                      limit: 10,
                      start: index,
                    })
                  }
                />
              ) : (
                <History
                  history={history}
                  select={(project) => {
                    selectProject(project);
                    hide();
                  }}
                />
              )}
            </div>
          </div>
        ),
      })}
    </SelectList>
  );
}

export { ProjectHistory };
